import React from "react";
import { Camera, Film, AudioLines, Maximize2 } from "lucide-react";
import MediaEmbed from "@/components/site/MediaEmbed";
import { Carousel } from "@/components/site/Carousel";
import { Lightbox } from "@/components/site/Lightbox";
import { Reveal, SafeImg } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";

const play = (id) => api.post(`/media/${id}/play`).catch(() => {});

function Block({ icon: Icon, label, count, children, testId }) {
  if (!count) return null;
  return (
    <Reveal className="mt-12 first:mt-0" data-testid={testId}>
      <div className="flex items-center gap-3 mb-5" data-testid={`${testId}-heading`}>
        <Icon className="h-4 w-4 text-copper" />
        <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-copperlight">{label}</h3>
        <span className="h-px flex-1 bg-copper/20" />
        <span className="font-mono text-[10px] text-sand">{count}</span>
      </div>
      {children}
    </Reveal>
  );
}

// Sezione unificata "Edizioni precedenti": tre mini-sezioni (Foto / Video / Audio) a scorrimento.
export default function PastEditions({ title, photos = [], videos = [], audio = [], alt = "", testId = "past-editions", className }) {
  const { t } = useLang();
  const [lightIdx, setLightIdx] = React.useState(null);
  if (!photos.length && !videos.length && !audio.length) return null;
  return (
    <section className={"py-20 sm:py-28 bg-obsidian2 border-t border-white/5 " + (className || "")} data-testid={testId}>
      <div className="container-x">
        <Reveal>
          <p className="eyebrow mb-10">{t("past.title")}</p>
        </Reveal>
        <Block icon={Camera} label={`${t("past.title")} → ${t("past.photos")}`} count={photos.length} testId={`${testId}-photos`}>
          <Carousel testId={`${testId}-photos-carousel`}>
            {photos.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightIdx(i)}
                className="block w-full overflow-hidden border border-white/5 group relative cursor-zoom-in"
                aria-label={`Ingrandisci foto ${i + 1}`}
                data-testid={`${testId}-photo-${i}`}
              >
                <SafeImg src={img} alt={`${alt} — ${i + 1}`} className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <span className="absolute inset-0 bg-obsidian/0 group-hover:bg-obsidian/25 transition-colors duration-500 flex items-center justify-center">
                  <Maximize2 className="h-6 w-6 text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </span>
              </button>
            ))}
          </Carousel>
        </Block>
        <Block icon={Film} label={`${t("past.title")} → ${t("past.videos")}`} count={videos.length} testId={`${testId}-videos`}>
          <Carousel itemClass="w-[84vw] sm:w-[60vw] lg:w-[48%]" testId={`${testId}-videos-carousel`}>
            {videos.map((m) => <MediaEmbed key={m.id} media={m} onPlay={play} />)}
          </Carousel>
        </Block>
        <Block icon={AudioLines} label={`${t("past.title")} → ${t("past.audio")}`} count={audio.length} testId={`${testId}-audio`}>
          <Carousel itemClass="w-[84vw] sm:w-[46vw] lg:w-[31%]" testId={`${testId}-audio-carousel`}>
            {audio.map((m) => <MediaEmbed key={m.id} media={m} onPlay={play} className="h-full" />)}
          </Carousel>
        </Block>
      </div>
      <Lightbox images={photos} index={lightIdx} alt={alt} onClose={() => setLightIdx(null)} onIndex={setLightIdx} testId={`${testId}-lightbox`} />
    </section>
  );
}
